import DOMPurify from "dompurify";
import { marked } from "marked";
import { useMemo, useState } from "react";

import {
  parseArtefactSections,
  parseChecklistFromMd,
  stripChecklistFence,
  type ChecklistItem,
} from "./artefact-preview-utils.js";

/**
 * Rich artefact preview for Ask Insights.
 * - checklist: interactive local toggles over fenced ```json checklist```
 * - brief / memo / faq: structured section chrome over markdown body
 * - other kinds: sanitised GFM markdown
 */

function KindBadge({ kind }: { kind: string }) {
  const label =
    kind === "brief"
      ? "Brief"
      : kind === "memo"
        ? "Memo"
        : kind === "faq"
          ? "FAQ"
          : kind === "checklist"
            ? "Checklist"
            : null;
  if (!label) return null;
  return (
    <span className="inline-flex rounded border border-border bg-surface-muted px-2 py-0.5 text-xs font-medium uppercase tracking-wide text-fg-muted">
      {label}
    </span>
  );
}

function MarkdownBlock({ md }: { md: string }) {
  const html = useMemo(
    () => DOMPurify.sanitize(marked.parse(md || "", { async: false, gfm: true }) as string),
    [md],
  );
  return <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: html }} />;
}

function StructuredArtefact({ bodyMd, kind }: { bodyMd: string; kind: string }) {
  const sections = useMemo(() => parseArtefactSections(bodyMd), [bodyMd]);
  const isFaq = kind === "faq";

  return (
    <div className="space-y-3">
      <KindBadge kind={kind} />
      <div className="space-y-3">
        {sections.map((section, i) => (
          <section
            key={`${i}:${section.heading}`}
            className="rounded-md border border-border bg-surface-muted/40 p-3"
          >
            <h4
              className={
                isFaq
                  ? "text-sm font-semibold text-fg"
                  : "text-xs font-semibold uppercase tracking-wide text-fg-muted"
              }
            >
              {section.heading}
            </h4>
            <div className="mt-2">
              <MarkdownBlock md={section.body} />
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

function InteractiveChecklist({ items, proseMd }: { items: ChecklistItem[]; proseMd: string }) {
  const [local, setLocal] = useState(items);
  const doneCount = local.filter((i) => i.done).length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <KindBadge kind="checklist" />
        <span className="text-xs text-fg-muted">
          {doneCount}/{local.length} done (local — not persisted)
        </span>
      </div>
      <ul className="space-y-1.5 rounded-md border border-border bg-surface-muted p-3">
        {local.map((item, i) => (
          <li key={`${i}:${item.text}`} className="flex items-start gap-2 text-sm text-fg">
            <input
              type="checkbox"
              checked={item.done}
              className="mt-0.5"
              onChange={() => {
                setLocal((prev) =>
                  prev.map((row, idx) => (idx === i ? { ...row, done: !row.done } : row)),
                );
              }}
            />
            <span className={item.done ? "text-muted-fg line-through" : undefined}>
              {item.text}
            </span>
          </li>
        ))}
      </ul>
      {proseMd ? <MarkdownBlock md={proseMd} /> : null}
    </div>
  );
}

export function ArtefactPreview({ bodyMd, kind }: { bodyMd: string; kind?: string }) {
  const checklist =
    kind === "checklist" || bodyMd.includes("```json checklist") || bodyMd.includes("```checklist")
      ? parseChecklistFromMd(bodyMd)
      : null;

  if (checklist && checklist.length > 0) {
    return <InteractiveChecklist items={checklist} proseMd={stripChecklistFence(bodyMd)} />;
  }

  if (kind === "brief" || kind === "memo" || kind === "faq") {
    return <StructuredArtefact bodyMd={bodyMd} kind={kind} />;
  }

  return (
    <div className="space-y-2">
      {kind && kind !== "note" && kind !== "other" ? <KindBadge kind={kind} /> : null}
      <MarkdownBlock md={bodyMd} />
    </div>
  );
}
